import React, { useEffect, useRef, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { createContactFile } from './actions/create-contact-file';
import { deleteContactFile } from './actions/delete-contact-file';
import { getContactFileUrl } from './actions/get-contact-file-url';
import { loadContactFiles } from './actions/load-contact-files';
import {
  contactFilesForContactIdSelector,
  contactFilesStateForContactIdSelector,
} from './contact-file-selectors';
import { loadContactMessages } from '../contacts/actions/load-contact-messages';
import { loadOrg } from '../organization/actions/load-org';
import { messagesWithImagesForContactIdSelector } from '../reducers/contact-selectors';
import { orgSelector } from '../reducers/org-selectors';

import {
  S3_DISALLOWED_CHARACTER_REGEX,
  uploadFileViaEmployer,
} from '../api/file-upload-api-client';
import { UiButton } from '../ui/ui-button';

import { ReactComponent as EmployeeFilesEmptyIllustration } from './components/empty-files-illustration.svg';
import { ImageFromMessage } from './components/image-from-message';
import { UploadedFile } from './components/uploaded-file';

import { EMPLOYEE_PERMISSION, HIRING_PERMISSION } from '../util/permissions';
import { SORT_ORDER, sortByDate } from '../util/sort-functions';
import { confirm } from '../widgets/confirmation-dialog';
import MessageWidget from '../widgets/legacy-message';
import { LoadingMessage } from '../widgets/loading-message';

type InputProps = {
  user: AuthenticatedUser;
  contactId: number;
  orgId: number;
  employeeId?: number;
  jobApplicantId?: number;
};

const mapStateToProps = (state: any, { contactId, orgId }: InputProps) => ({
  org: orgSelector(state, orgId),
  contactFiles: contactFilesForContactIdSelector(state, contactId),
  contactFilesState: contactFilesStateForContactIdSelector(state, contactId),
  messagesWithImages: messagesWithImagesForContactIdSelector(state, contactId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { contactId, orgId }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    getContactFileUrl: (contactFileId: number) => dispatch(getContactFileUrl(contactFileId)),
    deleteContactFile: (contactFileId: number) => dispatch(deleteContactFile(contactFileId)),
    createContactFile: (contactFile: ContactFileCreateRequest) =>
      dispatch(createContactFile(contactFile)),
    loadContactFiles: () => dispatch(loadContactFiles(contactId)),
    loadContactMessages: () => dispatch(loadContactMessages(contactId)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

const isContactFile = (obj: any): obj is ContactFile => obj.contactFileId;

export const ContactFiles = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    user,
    contactId,
    employeeId,
    jobApplicantId,
    org,
    contactFiles,
    contactFilesState,
    messagesWithImages,
    actions,
  } = props;

  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const [sizeError, setSizeError] = useState<string>('');
  const inputRef = useRef<any>();

  useEffect(() => {
    actions.loadOrg();
    actions.loadContactMessages();
    actions.loadContactFiles();
  });

  if (!org || !contactFilesState || contactFilesState.loading || !messagesWithImages) {
    return <LoadingMessage />;
  }

  const hasUploadPermission =
    user.hiringPermission === HIRING_PERMISSION.ALL ||
    user.employeesPermission === EMPLOYEE_PERMISSION.ALL;

  const handleAddClick = () => {
    inputRef.current.click();
  };

  const handleUploadClick = async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
    setSizeError('');
    if (!files || files.length === 0) {
      return;
    }

    const s3SafeFileName = files[0].name.replace(S3_DISALLOWED_CHARACTER_REGEX, '_');
    const file = new File([files[0]], s3SafeFileName, { type: files[0].type });

    const fileSizeInMb = file.size / 1024 / 1024;
    if (fileSizeInMb > 25) {
      setSizeError('File size cannot exceed 25mb');
      inputRef.current.value = '';
      return;
    }
    if (contactFiles.find((f) => f.fileName === file.name)) {
      if (
        !(await confirm(
          `Uploading this file will overwrite the "${file.name}" you already have for this person. Are you sure you want to do that?`,
        ))
      ) {
        inputRef.current.value = '';
        return;
      }
    }
    setIsUploadingFile(true);
    const filePath = `contact-files/${org.org_key}/${contactId}/${file.name}`;

    try {
      const response = await uploadFileViaEmployer(filePath, file);
      if (!response.ok) {
        setIsUploadingFile(false);
        return;
      }
    } catch (e) {
      setIsUploadingFile(false);
      return;
    }

    await actions.createContactFile({
      employeeId,
      jobApplicantId,
      contactId,
      fileName: file.name,
    });

    setIsUploadingFile(false);
  };

  const allFiles = ([] as Array<ContactFile | Message>)
    .concat(contactFiles)
    .concat(messagesWithImages);
  const allSortedFiles = allFiles.sort(
    sortByDate(
      (file) => (isContactFile(file) ? file.createdAt : file.activity.datetime),
      SORT_ORDER.DESC,
    ),
  );

  return (
    <div>
      {sizeError && (
        <MessageWidget
          canBeDismissed={false}
          className="u-margin-bottom"
          variant="error"
          message={sizeError}
        />
      )}
      {allSortedFiles.length === 0 && (
        <>
          <EmployeeFilesEmptyIllustration className="u-maxWidth-300px u-display-block u-margin-0Auto" />
          <div className="typo-h6 u-textAlign-center u-margin-y-lg">No documents yet</div>
        </>
      )}
      {hasUploadPermission ? (
        <div
          className={`u-display-flex u-margin-bottom-lg ${
            allSortedFiles.length === 0 ? 'u-justifyContent-center' : 'u-justifyContent-flexEnd'
          }`}
        >
          <input ref={inputRef} hidden type="file" onChange={handleUploadClick} />
          <UiButton loading={isUploadingFile} disabled={isUploadingFile} onClick={handleAddClick}>
            {isUploadingFile ? 'Uploading...' : 'Add Document'}
          </UiButton>
        </div>
      ) : null}
      {allSortedFiles.map((file) =>
        isContactFile(file) ? (
          <UploadedFile key={file.contactFileId} contactFile={file} />
        ) : (
          <ImageFromMessage key={file.messageId} message={file} />
        ),
      )}
    </div>
  );
});
